import { CMSSubscriptionInterface, DoceboSubscriptionInterface } from '@app/interfaces/_index';
import {
  CardTypeCallToActions,
  ProductShortName,
  RouteTypes,
  SubscriptionName,
  SubscriptionType,
} from '@app/enums/_index';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { SubscriptionService, WordpressSubscriptionsService } from '@app/services/_index';

import { LMSSubscription } from '@app/models/_index';
import { Subscription } from 'rxjs';
import { SubscriptionAdapter } from '@app/adapters/subscription.adapter';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-subscription-accordion',
  templateUrl: './subscription-accordion.component.html',
  styleUrls: ['./subscription-accordion.component.scss'],
  standalone: false,
})
export class SubscriptionAccordionComponent implements OnInit, OnDestroy {
  voucherLookupForm: FormGroup;
  @Output() voucherSubmit = new EventEmitter<LMSSubscription>();

  // Toggle docebo subscription content vs dynamic content
  loadDoceboSubscriptionContent = false;
  subscriptionItems: LMSSubscription[] = [];
  isLoading: boolean;
  subscriptionIds: number[] = environment.beyond.subscription_ids;
  toggleDropdown = '';
  isVoucherCode = false;
  voucherCodeLimit = 6;

  sub: Subscription;
  subs: Subscription[] = [];

  constructor(
    private subscriptionService: SubscriptionService,
    private wpSubscriptionService: WordpressSubscriptionsService,
    private formBuilder: FormBuilder,
    private subsAdapter: SubscriptionAdapter
  ) {
    this.voucherLookupForm = this.formBuilder.group({
      voucherCode: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.getSubscriptions();
  }

  /**
   * Set and load CMS or Docebo Subscriptions
   */
  getSubscriptions(): void {
    // checking loading from decobo or WordPress CMS
    if (!this.loadDoceboSubscriptionContent) {
      // Default WordPress CMS
      this.isLoading = true;
      this.sub = this.wpSubscriptionService.getSubscriptionList().subscribe({
        next: (subscriptionItems: CMSSubscriptionInterface[]) => {
          // Sort the subscription cards based on display order acf field.
          subscriptionItems.sort((a, b) =>
            a['acf'].display_order
              .toLocaleString()
              .localeCompare(b['acf'].display_order.toLocaleString())
          );
          this.isLoading = false;
          // TODO: Use rxjs map
          subscriptionItems.forEach((item: CMSSubscriptionInterface) => {
            // NOTE: Feature flag Pointillist
            const subscriptionItem: LMSSubscription = this.subsAdapter.adapt(item);
            if (subscriptionItem) {
              if (
                subscriptionItem.product !== ProductShortName.AI_ESSENTIALS &&
                subscriptionItem.product !== ProductShortName.POINTILLIST &&
                subscriptionItem.product !== ProductShortName.AGENT_PLATFORM &&
                subscriptionItem.product !== ProductShortName.GENESYS_CLOUD_TRIAL &&
                subscriptionItem.product !== ProductShortName.GENESYS_CLOUD_PREMIER_PARTNER
              ) {
                this.subscriptionItems.push(subscriptionItem);
              }
            }
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Subscription content completed');
        },
      });
    } else {
      // NOTE: Toggle dropdown needs to match slug name
      // Else pull subscription information from Docebo directly if CMS is unavailable
      this.sub = this.subscriptionService.getSubscriptions(this.subscriptionIds).subscribe({
        next: (items: DoceboSubscriptionInterface[]) => {
          this.isLoading = false;
          items
            .filter((nonulls) => nonulls)
            .forEach((item: DoceboSubscriptionInterface) => {
              const subscriptionItem = this.subsAdapter.adaptFromLMS(item);
              if (subscriptionItem) {
                this.subscriptionItems.push(subscriptionItem);
              }
            });
          // TODO: Add backup fallback for Agent Platform (AP) similar to BeyondCX
          const aiEssentialsSubscription = new LMSSubscription(
            -1,
            '',
            SubscriptionName.AI_ESSENTIAL_SUBSCRIPTION,
            'x',
            SubscriptionType.AI_ESSENTIAL_SUBSCRIPTION,
            '',
            'info-icon',
            4,
            ProductShortName.AI_ESSENTIALS,
            ProductShortName.AI_ESSENTIALS
          );
          this.subscriptionItems.push(aiEssentialsSubscription);
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Subscription content completed');
        },
      });
    }
  }

  /**
   * Toggle Navigation
   * @param subscription - the subscription selected to toggle accordion
   */
  onToggleNavigation(subscription: string): void {
    if (subscription === this.toggleDropdown) {
      this.toggleDropdown = '';
    } else {
      this.toggleDropdown = subscription;
    }
  }

  /**
   * Validates voucher code entered as input
   * @param voucherCode - Voucher code as input for validation.
   */
  onChange(voucherCode: string): void {
    if (voucherCode && voucherCode.length >= this.voucherCodeLimit) {
      this.isVoucherCode = true;
    }
  }

  /**
   * Emits true when Voucher submit button is clicked
   * @param subscription - selected subscription that will be used by voucher for redemption
   */
  onVoucherSubmit(subscription: LMSSubscription): void {
    this.voucherSubmit.emit(subscription);
  }

  /**
   * Add Track By Subscription Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of enrollment array
   * @param subscription - Subscription item
   * @returns enrollment id
   */
  trackBySubscriptionId(index: number, subscription: LMSSubscription): number {
    if (subscription) {
      return subscription.id;
    } else {
      return index;
    }
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }
  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
